import { createFileRoute, Link } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import { convexQuery } from '@convex-dev/react-query'
import { useMutation } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import * as React from 'react'

export const Route = createFileRoute('/w/$workspaceId')({
  component: WorkspacePage,
})

function WorkspacePage() {
  const { workspaceId } = Route.useParams()
  const { data: workspace } = useSuspenseQuery(
    convexQuery(api.workspaces.workspaceById, { workspaceId: workspaceId as any })
  )
  const { data: docs } = useSuspenseQuery(
    convexQuery(api.docs.listDocsInWorkspace, { workspaceId: workspaceId as any })
  )
  const { data: members } = useSuspenseQuery(
    convexQuery(api.workspaces.listMembers, { workspaceId: workspaceId as any })
  )
  const { data: credits } = useSuspenseQuery(
    convexQuery(api.workspaces.creditsByWorkspace, { workspaceId: workspaceId as any })
  )
  const createDoc = useMutation(api.docs.createDoc)
  const [creating, setCreating] = React.useState(false)
  const [showCreateModal, setShowCreateModal] = React.useState(false)
  const [docTitle, setDocTitle] = React.useState('')
  const inviteMember = useMutation(api.workspaces.inviteMemberByEmail)
  const [showShareModal, setShowShareModal] = React.useState(false)
  const [inviting, setInviting] = React.useState(false)
  const [inviteEmail, setInviteEmail] = React.useState('')
  const [inviteMessage, setInviteMessage] = React.useState<string | null>(null)

  const onCreate = async () => {
    if (!docTitle.trim()) return
    setCreating(true)
    try {
      await createDoc({ workspaceId: workspaceId as any, title: docTitle.trim() })
      setDocTitle('')
      setShowCreateModal(false)
    } finally {
      setCreating(false)
    }
  }

  const onInvite = async () => {
    if (!inviteEmail.trim()) return
    setInviting(true)
    setInviteMessage(null)
    try {
      const result: any = await inviteMember({
        workspaceId: workspaceId as any,
        email: inviteEmail.trim(),
        role: 'editor',
      })
      if (result && result.message) {
        setInviteMessage(result.message)
      } else {
        setInviteMessage('Invitation processed.')
      }
      if (result && result.ok && result.reason === 'added') {
        setInviteEmail('')
      }
    } catch (error: any) {
      setInviteMessage(error?.message ?? 'Something went wrong while inviting.')
    } finally {
      setInviting(false)
    }
  }

  if (!workspace) {
    return (
      <div className="min-h-screen bg-[#fafafa] flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Workspace not found or access denied</p>
          <Link to="/app" className="text-blue-600 hover:underline mt-4 inline-block">
            ← Back to workspaces
          </Link>
        </div>
      </div>
    )
  }

  const isOwner = (workspace as any).isOwner ?? false

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-3 mb-2">
            <Link to="/app" className="text-sm text-gray-600 hover:text-gray-900">
              ← Workspaces
            </Link>
            <span className="text-gray-300">/</span>
            <span className="text-sm text-gray-900 font-medium">{workspace.name}</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">{workspace.name}</h2>
            <p className="text-gray-600">Documents and research briefs</p>
            {credits && (
              <p className="mt-1 text-xs text-gray-500">
                Credits remaining: <span className="font-semibold text-gray-800">{credits.balance}</span>
              </p>
            )}
            {members && members.length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-gray-500">
                <span className="font-medium text-gray-700">Members:</span>
                {members.map((m: any) => (
                  <span
                    key={m._id}
                    className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-xs text-gray-700"
                  >
                    <span>{m.email || 'User'}</span>
                    {m.isOwner && (
                      <span className="ml-1 text-[10px] uppercase text-blue-600">Owner</span>
                    )}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            {isOwner && (
              <button
                type="button"
                onClick={() => {
                  setInviteEmail('')
                  setInviteMessage(null)
                  setShowShareModal(true)
                }}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 8a3 3 0 11-6 0 3 3 0 016 0zM6 9a2 2 0 114 0 2 2 0 01-4 0zm10 8a4 4 0 00-8 0v1h8v-1zM4 17a3 3 0 016 0v1H4v-1z" />
                </svg>
                Share
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setDocTitle('')
                setShowCreateModal(true)
              }}
              disabled={creating}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Document
            </button>
          </div>
        </div>

        {docs.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-lg border-2 border-dashed border-gray-300">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No documents yet</h3>
            <p className="text-gray-600 mb-6">Create your first document to start writing</p>
            <button
              onClick={() => {
                setDocTitle('')
                setShowCreateModal(true)
              }}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Document
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {docs.map((d: any) => (
              <Link
                key={d._id}
                to="/d/$docId"
                params={{ docId: d._id }}
                className="group bg-white border border-gray-200 rounded-lg p-6 hover:border-blue-500 hover:shadow-md transition-all flex items-center justify-between"
              >
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">
                      {d.title || 'Untitled'}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>Updated {new Date(d._creationTime).toLocaleDateString()}</span>
                      {d.publishedSlug && (
                        <span className="flex items-center gap-1 text-green-600">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Published
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            ))}
          </div>
        )}
      </main>

      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Share workspace</h2>
            <p className="text-sm text-gray-600 mb-4">
              Invite teammates by email. They must have already signed in to Weblens with this email.
            </p>
            <input
              autoFocus
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="teammate@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 placeholder-gray-400"
            />
            {inviteMessage && (
              <p className="mt-3 text-sm text-gray-700">{inviteMessage}</p>
            )}
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  if (!inviting) {
                    setShowShareModal(false)
                    setInviteEmail('')
                    setInviteMessage(null)
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-md"
              >
                Close
              </button>
              <button
                type="button"
                disabled={inviting || !inviteEmail.trim()}
                onClick={() => void onInvite()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {inviting ? 'Inviting…' : 'Invite'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">New Document</h2>
            <p className="text-sm text-gray-600 mb-4">Give your document a clear title so it&apos;s easy to find later.</p>
            <input
              autoFocus
              value={docTitle}
              onChange={(e) => setDocTitle(e.target.value)}
              placeholder="e.g. Arctic Ice Analysis, Q4 Market Report"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 placeholder-gray-400"
            />
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  if (!creating) {
                    setShowCreateModal(false)
                    setDocTitle('')
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-md"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={creating || !docTitle.trim()}
                onClick={() => void onCreate()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creating ? 'Creating…' : 'Create document'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
